import { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import ApplePayMark from "chameleon/icons/apple-pay-mark";
import Ecp from "chameleon/icons/ecp";
import Gift from "chameleon/icons/gift";
import GooglePayMark from "chameleon/icons/google-pay-mark";
import PayByBank from "chameleon/icons/pay-by-bank";
import Paze from "chameleon/icons/paze";
import Sepa from "chameleon/icons/sepa";
import {
  CreditCardNetwork,
  DisplayCards,
} from "chameleon/lib/components/display-cards/display-cards";
import { RadioOption } from "chameleon/lib/components/radio-group/radio-group";

import { RADIO_GROUP_PAYMENT_METHODS_TYPE } from "checkout-experience/components/payment-method/constants";
import LpmIcon from "checkout-experience/components/payment-method/pay-options/lpm/lpm-icon/lpm-icon";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useEnabledPaymentMethods } from "checkout-experience/hooks/use-enabled-payment-methods";
import { radioGroupTrailingSlotImg } from "checkout-experience/hooks/use-payment-methods-radio-group-options.css";
import { useSupportedPaymentTypes } from "checkout-experience/hooks/use-supported-payment-types";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentType } from "checkout-query-client/types/form-types";

type PaymentMethodIconProps = {
  option: PaymentType;
  supportedPaymentTypes?: CreditCardNetwork[];
};

export const PaymentMethodIcon = ({
  option,
  supportedPaymentTypes,
}: PaymentMethodIconProps) => {
  const { t } = useTranslation(["combined"]);

  const displayCardsContainerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [justifyContent, setJustifyContent] = useState("normal");

  const translatedValues = useMemo(
    () => ({
      noCard: t("cardOptions.noCard"),
      americanExpress: t("cardOptions.americanExpress"),
      cvv: t("cardOptions.cvv"),
      discover: t("cardOptions.discover"),
      jcb: t("cardOptions.jcb"),
      visa: t("cardOptions.visa"),
      mastercard: t("cardOptions.mastercard"),
      maestro: t("cardOptions.maestro"),
      applePay: t("cardOptions.applePay"),
      unionPay: t("cardOptions.unionPay"),
      dinersClub: t("cardOptions.dinersClub"),
      cartesBancaires: t("cardOptions.cartesBancaires"),
      tooltipAriaLabel: t("tooltipAriaLabel"),
    }),
    [t]
  );

  useEffect(() => {
    if (!displayCardsContainerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      if (displayCardsContainerRef?.current) {
        // set width to pass to <DisplayCards />
        setWidth(entries[0].contentRect.width);
        // if the x coordinate is greater than 100 that means it hasn't wrapped
        setJustifyContent(
          displayCardsContainerRef?.current?.getBoundingClientRect().x > 100
            ? "flex-end"
            : "normal"
        );
      }
    });
    observer.observe(displayCardsContainerRef.current);
    return () => {
      if (displayCardsContainerRef?.current) {
        observer.unobserve(displayCardsContainerRef.current);
      }
    };
  }, []);

  switch (option) {
    case PaymentType.applePay:
      return (
        <ApplePayMark
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.googlePay:
      return (
        <GooglePayMark
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.creditCard:
      return (
        <div
          ref={displayCardsContainerRef}
          style={{ flex: 1 }}
          aria-label={t("acceptedCards") ?? ""}
        >
          <DisplayCards
            maxWidth={width ? width : 0}
            justifyContent={justifyContent}
            supportedPaymentTypes={supportedPaymentTypes}
            translatedValues={translatedValues}
          />
        </div>
      );
    case PaymentType.eCheck:
      return (
        <Ecp
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.sepa:
      return (
        <Sepa
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.paze:
      return (
        <Paze
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.payByBank:
      return (
        <PayByBank
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.giftCard:
      return (
        <Gift
          sizeVariant={24}
          isRectangle
          aria-hidden="true"
          role={"presentation"}
        />
      );
    case PaymentType.ideal:
    case PaymentType.alipay:
    case PaymentType.trustly:
    case PaymentType.wechatPay:
    case PaymentType.paypal:
      return <LpmIcon paymentType={option} />;
    default:
      return null;
  }
};

export const getPaymentMethodLabel = (option: PaymentType, t: TFunction) => {
  switch (option) {
    case PaymentType.creditCard:
      return t("creditCard");
    case PaymentType.eCheck:
      return t("eCheck");
    case PaymentType.applePay:
      return t("applePay.applePay");
    case PaymentType.googlePay:
      return t("googlePay.googlePay");
    case PaymentType.sepa:
      return t("sepa");
    case PaymentType.paze:
      return t("paze.paze");
    case PaymentType.payByBank:
      return t("payByBank.payByBank");
    case PaymentType.ideal:
      return t("ideal");
    case PaymentType.alipay:
      return t("alipay");
    case PaymentType.wechatPay:
      return t("wechatPay");
    case PaymentType.trustly:
      return t("trustly");
    case PaymentType.paypal:
      return t("paypal");
    case PaymentType.giftCard:
      return t("giftCard.title");
    default:
      return "";
  }
};

export const usePaymentMethodsRadioGroupOptions = (
  surchargeText?: string
): {
  radioGroupOptions: RadioOption[];
} => {
  const { paymentOption } = usePaymentOptionContext() ?? {};
  const { t } = useTranslation(["combined"]);
  const { paymentMethodsList } = useEnabledPaymentMethods();
  const { checkoutSession } = useChexContext();
  const supportedPaymentTypes = useSupportedPaymentTypes(
    checkoutSession?.currencyCode ?? ""
  );

  const radioGroupOptions = paymentMethodsList
    .filter((paymentMethod) => paymentMethod.active === true)
    .map((paymentMethod) => {
      return {
        id: paymentMethod.name,
        type: RADIO_GROUP_PAYMENT_METHODS_TYPE.newPaymentMethods,
        label: getPaymentMethodLabel(paymentMethod.name, t),
        trailingSlot: (
          <span className={radioGroupTrailingSlotImg}>
            <PaymentMethodIcon
              supportedPaymentTypes={supportedPaymentTypes}
              option={paymentMethod.name}
            />
          </span>
        ),
        messages:
          paymentMethod.name === paymentOption?.formId && surchargeText
            ? [surchargeText]
            : [],
      };
    });

  return {
    radioGroupOptions,
  };
};
